// GBG Smartcapture / AssureID RTK Query slice.
//
// Separate createApi with its own reducerPath/middleware: different base URL
// (WEB_SDK.ID_ENDPOINT) + Basic auth, and NO retry — retries are intentionally
// dropped so failures surface to the caller. Spec-sanctioned exception to the
// single-apiSlice rule.
//
// Uses a thin axios baseQuery (Basic-auth + accept-language headers,
// per-endpoint responseType for the binary image fetches).

import { createApi } from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import { acuantConfig } from 'config';
import { govtIdApi } from 'features/govtId/api';
import { IMAGE_CROPPING_MODE } from 'features/govtId/helper/constants';
import { attachConnectivity } from 'features/connectivity';

const pjson = require('../../../../package.json');
const { WEB_SDK } = acuantConfig;

const smartCaptureClient = axios.create({ baseURL: WEB_SDK.ID_ENDPOINT });

// Reported to the connection banner, which also holds a GET while the user is offline. No retry,
// in line with the rule above.
attachConnectivity(smartCaptureClient);

// Thin axios baseQuery — Basic auth + accept-language, no retry. `responseType`
// is passed through per-endpoint (arraybuffer for the binary image fetches).
const smartCaptureBaseQuery = async ({ url, method = 'GET', data, responseType }) => {
    try {
        const result = await smartCaptureClient({
            url,
            method,
            data,
            responseType,
            headers: {
                Authorization: `Basic ${WEB_SDK.AUTH_TOKEN}`,
                'accept-language': 'en-US',
            },
        });
        return { data: result.data };
    } catch (error) {
        return {
            error: {
                status: error.response?.status,
                data: error.response?.data ?? error.message,
            },
        };
    }
};

export const smartCaptureApi = createApi({
    reducerPath: 'smartCaptureApi',
    baseQuery: smartCaptureBaseQuery,
    tagTypes: ['SmartCaptureDocument'],
    endpoints: (builder) => ({
        getDocInstance: builder.mutation({
            query: ({ croppingMode = IMAGE_CROPPING_MODE.ALWAYS } = {}) => ({
                url: '/AssureIDService/Document/Instance',
                method: 'POST',
                data: {
                    AuthenticationSensitivity: 0,
                    TamperSensitivity: 0,
                    ClassificationMode: 0,
                    Device: {
                        HasContactlessChipReader: false,
                        HasMagneticStripeReader: false,
                        SerialNumber: 'JavaScriptWebSDK ' + pjson.version,
                        Type: {
                            Manufacturer: 'xxx',
                            Model: 'xxx',
                            SensorType: 3,
                        },
                    },
                    ImageCroppingExpectedSize: 0,
                    ImageCroppingMode: croppingMode,
                    ManualDocumentType: null,
                    ProcessMode: 0,
                    SubscriptionId: WEB_SDK.SUBSCRIPTION_ID,
                },
            }),
            invalidatesTags: ['SmartCaptureDocument'],
        }),

        postImage: builder.mutation({
            query: ({ instanceID, side, file }) => ({
                url: `/AssureIDService/Document/${instanceID}/Image?side=${side}&light=0&metrics=true`,
                method: 'POST',
                data: file,
            }),
        }),

        replaceImage: builder.mutation({
            query: ({ instanceID, side, file }) => ({
                url: `/AssureIDService/Document/${instanceID}/Image?side=${side}&light=0&metrics=true`,
                method: 'PUT',
                data: file,
            }),
        }),

        getClassification: builder.query({
            query: (instanceID) => ({
                url: `/AssureIDService/Document/${instanceID}/Classification`,
                method: 'GET',
            }),
            providesTags: ['SmartCaptureDocument'],
        }),

        getImageQualityMetric: builder.query({
            query: ({ instanceID, side }) => ({
                url: `/AssureIDService/Document/${instanceID}/Image/Metrics?side=${side}&light=0`,
                method: 'GET',
            }),
            providesTags: ['SmartCaptureDocument'],
        }),

        getFaceImage: builder.query({
            query: (instanceID) => ({
                url: `/AssureIDService/Document/${instanceID}/Field/Image?key=Photo`,
                method: 'GET',
                responseType: 'arraybuffer',
            }),
        }),

        getSignatureImage: builder.query({
            query: (instanceID) => ({
                url: `/AssureIDService/Document/${instanceID}/Field/Image?key=Signature`,
                method: 'GET',
                responseType: 'arraybuffer',
            }),
        }),

        getResults: builder.query({
            query: (instanceID) => ({
                url: `/AssureIDService/Document/${instanceID}`,
                method: 'GET',
            }),
            providesTags: ['SmartCaptureDocument'],
            // Preserve the legacy getResults → dump(result) side-effect: dump the
            // result to the backend before it is consumed.
            onQueryStarted: async (_instanceID, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(govtIdApi.endpoints.dumpResult.initiate(data));
                } catch {}
            },
        }),

        // Medical-card scan results. MedicScan lives on its own host (MEDICSCAN_ENDPOINT) but
        // shares the Basic auth token — an absolute URL makes axios ignore the ID_ENDPOINT baseURL.
        getMedicScanResults: builder.query({
            query: ({ instanceID, subscriptionID }) => ({
                url: `${WEB_SDK.MEDICSCAN_ENDPOINT}/api/v1/MedicalCard?subscriptionId=${subscriptionID}&instanceId=${instanceID}`,
                method: 'GET',
            }),
        }),
    }),
});

export const {
    useGetDocInstanceMutation,
    usePostImageMutation,
    useReplaceImageMutation,
    useLazyGetClassificationQuery,
    useLazyGetImageQualityMetricQuery,
    useLazyGetFaceImageQuery,
    useLazyGetSignatureImageQuery,
    useLazyGetResultsQuery,
    useLazyGetMedicScanResultsQuery,
} = smartCaptureApi;
